// frontend/src/pages/freelancer/FreelancerProducts.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import axios from "axios";
import { getStorefrontSetupStatus } from "../../services/storefrontService";
import StorefrontSetupStatusBanner from "../../components/storefront/StorefrontSetupStatusBanner";
import "./freelancerStyles.css";

const resolveProductImageUrl = (product, apiBaseUrl) => {
  const firstImage =
    (Array.isArray(product?.images) && product.images.length > 0
      ? product.images[0]
      : null) ||
    product?.imageUrl ||
    null;

  const rawImageUrl =
    typeof firstImage === "string"
      ? firstImage
      : firstImage?.url || firstImage?.imageUrl || "";

  if (!rawImageUrl) return "";
  if (/^https?:\/\//i.test(rawImageUrl) || rawImageUrl.startsWith("data:")) {
    return rawImageUrl;
  }

  const cleanBase = String(apiBaseUrl || "").replace(/\/+$/, "");
  const normalizedPath = rawImageUrl.startsWith("/")
    ? rawImageUrl
    : `/${rawImageUrl}`;
  return cleanBase ? `${cleanBase}${normalizedPath}` : rawImageUrl;
};

const resolveProductImageUrls = (product, apiBaseUrl, limit = 8) => {
  const images = Array.isArray(product?.images) ? product.images : [];
  const normalized = images
    .map((image) => {
      const rawImageUrl =
        typeof image === "string" ? image : image?.url || image?.imageUrl || "";
      if (!rawImageUrl) return "";
      if (/^https?:\/\//i.test(rawImageUrl) || rawImageUrl.startsWith("data:")) {
        return rawImageUrl;
      }
      const cleanBase = String(apiBaseUrl || "").replace(/\/+$/, "");
      const normalizedPath = rawImageUrl.startsWith("/")
        ? rawImageUrl
        : `/${rawImageUrl}`;
      return cleanBase ? `${cleanBase}${normalizedPath}` : rawImageUrl;
    })
    .filter(Boolean);

  return normalized.slice(0, limit);
};

function FreelancerProducts() {
  const navigate = useNavigate();
  const roles = JSON.parse(localStorage.getItem("ogestyle_roles") || "[]");
  const productBasePath = roles.includes("R7")
    ? "/dashboard/booth-renter/products"
    : "/dashboard/freelancer/products";
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [notifications, setNotifications] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [storefrontStatus, setStorefrontStatus] = useState(null);

  const isStorefrontSetupIncomplete =
    storefrontStatus &&
    Array.isArray(storefrontStatus.missingRequirements) &&
    storefrontStatus.missingRequirements.length > 0;
  const onboardingStage = storefrontStatus?.onboardingStage || "hard";
  const setupUiEnabled = onboardingStage !== "off";
  const softGateEnabled = onboardingStage === "soft";

  const confirmSoftPublishWarning = (actionLabel) => {
    if (!softGateEnabled) return true;
    if (!isStorefrontSetupIncomplete) return true;
    const missing = (
      storefrontStatus.missingRequirementLabels ||
      storefrontStatus.missingRequirements ||
      []
    ).join(", ");
    return window.confirm(
      `Storefront setup is incomplete (${missing}). You can continue for now, but publishing may be restricted later. Continue to ${actionLabel}?`,
    );
  };

  const handleStoreSetupBlockedError = (err) => {
    const code = err?.response?.data?.error;
    if (code !== "STORE_SETUP_INCOMPLETE") return false;

    const message =
      err?.response?.data?.message ||
      "Complete storefront setup before activating products.";
    const blocking =
      err?.response?.data?.blockingRequirementLabels ||
      err?.response?.data?.missingRequirementLabels ||
      [];
    const reasonLine =
      Array.isArray(blocking) && blocking.length > 0
        ? `\n\nBlocked by: ${blocking.join(", ")}`
        : "";
    const setupUrl =
      err?.response?.data?.setupUrl || `${productBasePath}/storefront-setup`;

    if (window.confirm(`${message}${reasonLine}\n\nOpen storefront setup now?`)) {
      navigate(setupUrl);
    }
    return true;
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error.");
      return null;
    }
    return baseURL;
  }, []);

  const toggleProductStatus = async (productId, currentStatus) => {
    try {
      if (!currentStatus && !confirmSoftPublishWarning("activate this product"))
        return;

      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      // Update product status
      await axios.put(
        `${baseURL}/api/freelancer/products/${productId}`,
        { isActive: !currentStatus },
        { headers },
      );

      // Refresh products list
      const res = await axios.get(`${baseURL}/api/freelancer/products`, {
        headers,
      });
      setProducts(res.data.products || []);
    } catch (err) {
      if (handleStoreSetupBlockedError(err)) {
        return;
      }
      setErrorMsg("Failed to update product status");
    }
  };

  // Bulk Operations
  const toggleProductSelection = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    );
  };

  const selectAll = () => {
    setSelectedProducts(products.map((p) => p._id || p.id));
  };

  const deselectAll = () => {
    setSelectedProducts([]);
  };

  const handleBulkActivate = async () => {
    if (selectedProducts.length === 0) return;
    if (
      !window.confirm(`Activate ${selectedProducts.length} selected products?`)
    )
      return;
    if (
      !confirmSoftPublishWarning(
        `activate ${selectedProducts.length} product(s)`,
      )
    )
      return;

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await Promise.all(
        selectedProducts.map((id) =>
          axios.put(
            `${baseURL}/api/v2/products/${id}`,
            { isActive: true },
            { headers },
          ),
        ),
      );
      alert(`${selectedProducts.length} products activated!`);
      setSelectedProducts([]);
      setSelectionMode(false);

      // Refresh
      const res = await axios.get(`${baseURL}/api/freelancer/products`, {
        headers,
      });
      setProducts(res.data.products || []);
    } catch (err) {
      if (handleStoreSetupBlockedError(err)) {
        return;
      }
      alert("Some products failed to activate");
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedProducts.length === 0) return;
    if (
      !window.confirm(
        `Deactivate ${selectedProducts.length} selected products?`,
      )
    )
      return;

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await Promise.all(
        selectedProducts.map((id) =>
          axios.put(
            `${baseURL}/api/v2/products/${id}`,
            { isActive: false },
            { headers },
          ),
        ),
      );
      alert(`${selectedProducts.length} products deactivated!`);
      setSelectedProducts([]);
      setSelectionMode(false);

      // Refresh
      const res = await axios.get(`${baseURL}/api/freelancer/products`, {
        headers,
      });
      setProducts(res.data.products || []);
    } catch (err) {
      alert("Some products failed to deactivate");
    }
  };

  const handleBulkStockUpdate = async () => {
    if (selectedProducts.length === 0) return;

    const quantityStr = prompt(
      `Enter stock adjustment for ${selectedProducts.length} products:\n\nEnter a number (e.g., 10 to add, -5 to remove):`,
    );
    if (quantityStr === null) return;

    const quantity = parseInt(quantityStr);
    if (isNaN(quantity) || quantity === 0) {
      alert("Please enter a valid non-zero number");
      return;
    }

    const action = quantity > 0 ? "add" : "remove";
    const absQuantity = Math.abs(quantity);

    if (
      !window.confirm(
        `${action === "add" ? "Add" : "Remove"} ${absQuantity} units ${action === "add" ? "to" : "from"} ${selectedProducts.length} products?`,
      )
    )
      return;

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const response = await axios.post(
        `${baseURL}/api/v2/products/bulk-stock-update`,
        {
          productIds: selectedProducts,
          adjustment: quantity,
          businessId:
            products.find((p) => selectedProducts.includes(p._id || p.id))
              ?.businessId?._id ||
            products.find((p) => selectedProducts.includes(p._id || p.id))
              ?.businessId,
        },
        { headers },
      );

      alert(
        `Successfully updated ${response.data.updatedCount || selectedProducts.length} products!`,
      );
      setSelectedProducts([]);
      setSelectionMode(false);

      // Refresh
      const res = await axios.get(`${baseURL}/api/freelancer/products`, {
        headers,
      });
      setProducts(res.data.products || []);
    } catch (err) {
      alert(err.response?.data?.message || "Some products failed to update");
    }
  };

  const handleBulkPriceUpdate = async () => {
    if (selectedProducts.length === 0) return;

    const adjustmentType = prompt(
      "Price adjustment type:\n\n1 = Set fixed price\n2 = Percentage increase/decrease\n3 = Dollar amount increase/decrease\n\nEnter 1, 2, or 3:",
    );
    if (adjustmentType === null) return;

    const type = parseInt(adjustmentType);
    if (![1, 2, 3].includes(type)) {
      alert("Please enter 1, 2, or 3");
      return;
    }

    let value, confirmMessage;

    if (type === 1) {
      const priceStr = prompt(
        "Enter new price for all selected products (e.g., 29.99):",
      );
      if (priceStr === null) return;
      value = parseFloat(priceStr);
      if (isNaN(value) || value < 0) {
        alert("Please enter a valid positive price");
        return;
      }
      confirmMessage = `Set price to $${value.toFixed(2)} for ${selectedProducts.length} products?`;
    } else if (type === 2) {
      const percentStr = prompt(
        "Enter percentage change (e.g., 10 for +10%, -15 for -15%):",
      );
      if (percentStr === null) return;
      value = parseFloat(percentStr);
      if (isNaN(value)) {
        alert("Please enter a valid percentage");
        return;
      }
      confirmMessage = `${value > 0 ? "Increase" : "Decrease"} prices by ${Math.abs(value)}% for ${selectedProducts.length} products?`;
    } else {
      const amountStr = prompt(
        "Enter dollar amount change (e.g., 5 for +$5, -3 for -$3):",
      );
      if (amountStr === null) return;
      value = parseFloat(amountStr);
      if (isNaN(value)) {
        alert("Please enter a valid amount");
        return;
      }
      confirmMessage = `${value > 0 ? "Add" : "Subtract"} $${Math.abs(value).toFixed(2)} ${value > 0 ? "to" : "from"} prices for ${selectedProducts.length} products?`;
    }

    if (!window.confirm(confirmMessage)) return;

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const response = await axios.post(
        `${baseURL}/api/v2/products/bulk-price-update`,
        {
          productIds: selectedProducts,
          adjustmentType: type,
          value,
          businessId:
            products.find((p) => selectedProducts.includes(p._id || p.id))
              ?.businessId?._id ||
            products.find((p) => selectedProducts.includes(p._id || p.id))
              ?.businessId,
        },
        { headers },
      );

      alert(
        `Successfully updated ${response.data.updatedCount || selectedProducts.length} products!`,
      );
      setSelectedProducts([]);
      setSelectionMode(false);

      // Refresh
      const res = await axios.get(`${baseURL}/api/freelancer/products`, {
        headers,
      });
      setProducts(res.data.products || []);
    } catch (err) {
      alert(err.response?.data?.message || "Some products failed to update");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) return;
    if (
      !window.confirm(
        `Delete ${selectedProducts.length} selected products? This cannot be undone!`,
      )
    )
      return;

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await Promise.all(
        selectedProducts.map((id) =>
          axios.delete(`${baseURL}/api/v2/products/${id}`, { headers }),
        ),
      );
      alert(`${selectedProducts.length} products deleted!`);
      setSelectedProducts([]);
      setSelectionMode(false);

      // Refresh
      const res = await axios.get(`${baseURL}/api/freelancer/products`, {
        headers,
      });
      setProducts(res.data.products || []);
    } catch (err) {
      alert("Some products failed to delete");
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;
        const res = await axios.get(`${baseURL}/api/freelancer/products`, {
          headers,
        });
        setProducts(res.data.products || []);

        // Fetch notifications
        try {
          const notifRes = await axios.get(`${baseURL}/api/notifications`, {
            headers,
          });
          const lowStockNotifs = (notifRes.data.notifications || []).filter(
            (n) => n.type === "low_stock" && !n.read,
          );
          setNotifications(lowStockNotifs);
        } catch (notifErr) {}
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem("ogestyle_token");
          navigate("/login");
        } else {
          setErrorMsg("Failed to load products");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  useEffect(() => {
    let mounted = true;

    const fetchStorefrontStatus = async () => {
      try {
        const data = await getStorefrontSetupStatus();
        if (mounted && data?.success) {
          setStorefrontStatus(data);
        }
      } catch (error) {}
    };

    fetchStorefrontStatus();

    return () => {
      mounted = false;
    };
  }, []);

  if (loading)
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading products...</p>
        </div>
      </FreelancerSidebarLayout>
    );

  const lowStockProducts = products.filter((p) => p.stock <= 5 && p.isActive);

  return (
    <FreelancerSidebarLayout>
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: isMobile ? "15px" : "20px",
        }}
      >
        {lowStockProducts.length > 0 && (
          <div
            style={{
              background: "#fef3c7",
              border: "1px solid #f59e0b",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "flex-start",
              gap: "12px",
            }}
          >
            <div style={{ fontSize: "20px" }}>⚠️</div>
            <div style={{ flex: 1 }}>
              <h4
                style={{
                  margin: "0 0 5px 0",
                  color: "#92400e",
                  fontSize: isMobile ? "14px" : "15px",
                  fontWeight: "600",
                }}
              >
                Low Stock Alert
              </h4>
              <p
                style={{
                  margin: 0,
                  color: "#78350f",
                  fontSize: isMobile ? "13px" : "14px",
                }}
              >
                {lowStockProducts.length} product
                {lowStockProducts.length > 1 ? "s have" : " has"} low stock (
                {lowStockProducts
                  .map((p) => `${p.name} (${p.stock})`)
                  .join(", ")}
                )
              </p>
            </div>
          </div>
        )}

        {setupUiEnabled && (
          <StorefrontSetupStatusBanner
            statusData={storefrontStatus}
            isMobile={isMobile}
            setupPath={`${productBasePath}/storefront-setup`}
          />
        )}

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            marginBottom: "30px",
            gap: "15px",
          }}
        >
          <div>
            <h2
              style={{
                margin: "0 0 8px 0",
                fontSize: isMobile ? "24px" : "28px",
              }}
            >
              My Products
            </h2>
            <p
              style={{
                margin: 0,
                color: "#666",
                fontSize: isMobile ? "14px" : "16px",
              }}
            >
              Sell products to your clients
            </p>
          </div>

          {!selectionMode ? (
            <div
              style={{
                display: "flex",
                gap: "10px",
                flexDirection: isMobile ? "column" : "row",
                width: isMobile ? "100%" : "auto",
              }}
            >
              {products.length > 0 && (
                <button
                  onClick={() => setSelectionMode(true)}
                  className="btn-secondary"
                  style={{
                    padding: isMobile ? "10px 20px" : "12px 24px",
                    fontSize: isMobile ? "14px" : "15px",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  ☑️ Select
                </button>
              )}
              <button
                onClick={() => navigate(`${productBasePath}/add`)}
                className="btn-primary"
                style={{
                  padding: isMobile ? "10px 20px" : "12px 24px",
                  fontSize: isMobile ? "14px" : "15px",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                + Add Product
              </button>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                alignItems: "center",
                width: isMobile ? "100%" : "auto",
              }}
            >
              {selectedProducts.length > 0 && (
                <span
                  style={{
                    background: "#3b82f6",
                    color: "#fff",
                    padding: "6px 12px",
                    borderRadius: "20px",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  {selectedProducts.length} selected
                </span>
              )}
              <button
                onClick={selectAll}
                className="btn-secondary"
                style={{ padding: "8px 16px", fontSize: "13px" }}
              >
                Select All
              </button>
              <button
                onClick={deselectAll}
                className="btn-secondary"
                style={{ padding: "8px 16px", fontSize: "13px" }}
              >
                Clear
              </button>
              {selectedProducts.length > 0 && (
                <>
                  <button
                    onClick={handleBulkActivate}
                    className="btn-success"
                    style={{
                      padding: "8px 16px",
                      fontSize: "13px",
                      background: "#10b981",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    ✓ Activate
                  </button>
                  <button
                    onClick={handleBulkDeactivate}
                    className="btn-warning"
                    style={{
                      padding: "8px 16px",
                      fontSize: "13px",
                      background: "#f59e0b",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    ✕ Deactivate
                  </button>
                  <button
                    onClick={handleBulkStockUpdate}
                    className="btn-info"
                    style={{
                      padding: "8px 16px",
                      fontSize: "13px",
                      background: "#6366f1",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    📦 Stock
                  </button>
                  <button
                    onClick={handleBulkPriceUpdate}
                    className="btn-info"
                    style={{
                      padding: "8px 16px",
                      fontSize: "13px",
                      background: "#8b5cf6",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    💰 Price
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="btn-danger"
                    style={{
                      padding: "8px 16px",
                      fontSize: "13px",
                      background: "#ef4444",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    🗑️ Delete
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setSelectionMode(false);
                  deselectAll();
                }}
                className="btn-secondary"
                style={{ padding: "8px 16px", fontSize: "13px" }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {errorMsg && (
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#c00" }}>{errorMsg}</p>
          </div>
        )}

        {products.length === 0 ? (
          <div
            className="dashboard-card"
            style={{
              padding: isMobile ? "30px 20px" : "40px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "40px" : "48px",
                marginBottom: "15px",
              }}
            >
              📦
            </div>
            <h3
              style={{
                color: "#666",
                marginBottom: "10px",
                fontSize: isMobile ? "18px" : "20px",
              }}
            >
              No Products Yet
            </h3>
            <p
              style={{
                color: "#999",
                marginBottom: "20px",
                fontSize: isMobile ? "14px" : "15px",
              }}
            >
              Start selling products by adding your first product.
            </p>
            <button
              onClick={() => navigate(`${productBasePath}/add`)}
              className="btn-primary"
              style={{ padding: isMobile ? "10px 24px" : "12px 28px" }}
            >
              Add Your First Product
            </button>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "20px",
            }}
          >
            {products.map((product) => {
              const imageUrl = resolveProductImageUrl(
                product,
                process.env.REACT_APP_API_BASE_URL,
              );
              const imageUrls = resolveProductImageUrls(
                product,
                process.env.REACT_APP_API_BASE_URL,
                Array.isArray(product?.images) ? product.images.length : 8,
              );
              const isSelected = selectedProducts.includes(
                product._id || product.id,
              );

              return (
                <div
                  key={product._id || product.id}
                  className="dashboard-card"
                  style={{
                    padding: "0",
                    overflow: "hidden",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    border: isSelected ? "3px solid #3b82f6" : "none",
                    cursor: selectionMode ? "pointer" : "default",
                  }}
                  onClick={() =>
                    selectionMode &&
                    toggleProductSelection(product._id || product.id)
                  }
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 16px rgba(0,0,0,0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  {imageUrl && (
                    <div
                      style={{
                        width: "100%",
                        height: isMobile ? "240px" : "260px",
                        overflow: "hidden",
                        background: "#f5f5f5",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <img
                        src={imageUrl}
                        alt={product.name}
                        style={{
                          width: "100%",
                          height: isMobile ? "180px" : "200px",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      {imageUrls.length > 1 && (
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            padding: "8px",
                            overflowX: "auto",
                            background: "#ffffff",
                            borderTop: "1px solid #e5e7eb",
                          }}
                        >
                          {imageUrls.map((url, index) => (
                            <div
                              key={`${product._id || product.id}-thumb-${index}`}
                              style={{
                                width: "42px",
                                height: "42px",
                                borderRadius: "6px",
                                overflow: "hidden",
                                flexShrink: 0,
                                border: index === 0 ? "2px solid #3b82f6" : "1px solid #d1d5db",
                              }}
                            >
                              <img
                                src={url}
                                alt={`${product.name} ${index + 1}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  display: "block",
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Checkbox Overlay */}
                      {selectionMode && (
                        <div
                          style={{
                            position: "absolute",
                            top: "10px",
                            left: "10px",
                            width: "28px",
                            height: "28px",
                            borderRadius: "6px",
                            background: isSelected
                              ? "#3b82f6"
                              : "rgba(255,255,255,0.9)",
                            border: isSelected ? "none" : "2px solid #999",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "16px",
                            fontWeight: "bold",
                            color: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          {isSelected ? "✓" : ""}
                        </div>
                      )}
                      {/* Status Badge */}
                      <div
                        style={{
                          position: "absolute",
                          top: "10px",
                          right: "10px",
                          padding: "4px 12px",
                          borderRadius: "12px",
                          background: product.isActive ? "#03543f" : "#666",
                          color: "#fff",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        {product.isActive ? "Active" : "Inactive"}
                      </div>
                    </div>
                  )}
                  {!imageUrl && (
                    <div
                      style={{
                        width: "100%",
                        height: isMobile ? "180px" : "200px",
                        overflow: "hidden",
                        background: "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <div style={{ fontSize: "48px", opacity: 0.3 }}>📦</div>
                      {/* Checkbox Overlay */}
                      {selectionMode && (
                        <div
                          style={{
                            position: "absolute",
                            top: "10px",
                            left: "10px",
                            width: "28px",
                            height: "28px",
                            borderRadius: "6px",
                            background: isSelected
                              ? "#3b82f6"
                              : "rgba(255,255,255,0.9)",
                            border: isSelected ? "none" : "2px solid #999",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "16px",
                            fontWeight: "bold",
                            color: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          {isSelected ? "✓" : ""}
                        </div>
                      )}
                      {/* Status Badge */}
                      <div
                        style={{
                          position: "absolute",
                          top: "10px",
                          right: "10px",
                          padding: "4px 12px",
                          borderRadius: "12px",
                          background: product.isActive ? "#03543f" : "#666",
                          color: "#fff",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        {product.isActive ? "Active" : "Inactive"}
                      </div>
                    </div>
                  )}
                  <div style={{ padding: "20px" }}>
                    <h3
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: isMobile ? "16px" : "18px",
                        color: "#001d4a",
                      }}
                    >
                      {product.name}
                    </h3>
                    {product.description && (
                      <p
                        style={{
                          margin: "0 0 12px 0",
                          fontSize: isMobile ? "13px" : "14px",
                          color: "#666",
                          lineHeight: "1.5",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {product.description}
                      </p>
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: "15px",
                        marginBottom: "15px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? "20px" : "22px",
                          fontWeight: "700",
                          color: "#001d4a",
                        }}
                      >
                        ${product.price?.toFixed(2) || "0.00"}
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "12px" : "13px",
                          color: product.stock <= 5 ? "#dc2626" : "#666",
                          fontWeight: product.stock <= 5 ? "600" : "400",
                        }}
                      >
                        Stock: {product.stock || 0}
                        {product.stock <= 5 && product.isActive && " ⚠️"}
                      </div>
                    </div>

                    {Array.isArray(product.variants) && product.variants.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "6px",
                          marginBottom: "12px",
                        }}
                      >
                        {product.variants.slice(0, 4).map((variant) => {
                          const variantStock = parseInt(variant.stock, 10) || 0;
                          const lowThreshold = parseInt(product.lowStockThreshold, 10) || 5;
                          const soldOut = variantStock <= 0 || variant.variantIsActive === false;
                          const low = !soldOut && variantStock <= lowThreshold;
                          return (
                            <span
                              key={variant.variantKey || variant._id}
                              style={{
                                fontSize: "10px",
                                padding: "3px 7px",
                                borderRadius: "999px",
                                background: soldOut
                                  ? "#fee2e2"
                                  : low
                                    ? "#fef3c7"
                                    : "#dcfce7",
                                color: soldOut
                                  ? "#991b1b"
                                  : low
                                    ? "#92400e"
                                    : "#166534",
                                border: `1px solid ${
                                  soldOut ? "#fecaca" : low ? "#fde68a" : "#bbf7d0"
                                }`,
                              }}
                            >
                              {variant.variantLabel || variant.variantKey}: {soldOut ? "Sold Out" : low ? `Low (${variantStock})` : variantStock}
                            </span>
                          );
                        })}
                        {product.variants.length > 4 && (
                          <span style={{ fontSize: "10px", color: "#6b7280" }}>
                            +{product.variants.length - 4} more variants
                          </span>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    {!selectionMode && (
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          marginTop: "15px",
                        }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `${productBasePath}/edit/${product._id || product.id}`,
                            );
                          }}
                          style={{
                            flex: 1,
                            padding: "8px 12px",
                            border: "1px solid #001d4a",
                            borderRadius: "6px",
                            background: "#001d4a",
                            color: "#fff",
                            fontSize: "13px",
                            fontWeight: "500",
                            cursor: "pointer",
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleProductStatus(
                              product._id || product.id,
                              product.isActive,
                            );
                          }}
                          style={{
                            flex: 1,
                            padding: "8px 12px",
                            border: product.isActive
                              ? "1px solid #ef4444"
                              : "1px solid #03543f",
                            borderRadius: "6px",
                            background: "#fff",
                            color: product.isActive ? "#ef4444" : "#03543f",
                            fontSize: "13px",
                            fontWeight: "500",
                            cursor: "pointer",
                          }}
                        >
                          {product.isActive ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </FreelancerSidebarLayout>
  );
}

export default FreelancerProducts;
